import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { resetPassword } from '../../Services/PasswordResetService';

interface FormData {
    password: string;
    confirmPassword: string;
}

interface Errors {
    password?: string;
    confirmPassword?: string;
    general?: string;
}

const SetPassword: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const email = searchParams.get('email') || '';

    const [formData, setFormData] = useState<FormData>({
        password: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState<Errors>({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name as keyof Errors]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validate = (): boolean => {
        const newErrors: Errors = {};

        if (!formData.password) {
            newErrors.password = 'La contraseña es obligatoria';
        } else if (formData.password.length < 8) {
            newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        } else if (!/[A-Z]/.test(formData.password)) {
            newErrors.password = 'La contraseña debe tener al menos 1 letra mayúscula';
        } else if (!/[a-z]/.test(formData.password)) {
            newErrors.password = 'La contraseña debe tener al menos 1 letra minúscula';
        } else if (!/[0-9]/.test(formData.password)) {
            newErrors.password = 'La contraseña debe tener al menos 1 número';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirma tu contraseña';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent): Promise<void> => {
        e.preventDefault();
        setErrors({});

        if (!token || !email) {
            setErrors({ general: 'Enlace de invitación no válido. Solicita uno nuevo al administrador.' });
            return;
        }

        if (!validate()) {
            return;
        }

        try {
            setLoading(true);
            await resetPassword(email, token, formData.password, formData.confirmPassword);
            setSuccess(true);
        } catch (err: any) {
            const backendError = err.response?.data?.error;
            setErrors({
                general: backendError || 'Error al configurar la cuenta. Inténtalo de nuevo.',
            });
        } finally {
            setLoading(false);
        }
    };

    if (!token || !email) {
        return (
            <div className="auth-container">
                <div className="login-form-container">
                    <div className="login-card">
                        <div className="login-card-body">
                            <div className="text-center mb-4">
                                <img src="/logos/logo-complet.avif" alt="Logo" className="logo" style={{ maxWidth: '100%' }} />
                            </div>
                            <div className="alert alert-danger" role="alert">
                                Enlace de invitación no válido. Solicita uno nuevo al administrador.
                            </div>
                            <div className="text-center mt-3">
                                <Link to="/" className="link-primary">
                                    Volver al inicio de sesión
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="login-form-container">
                <div className="login-card">
                    <div className="login-card-body">
                        {/* Logo */}
                        <div className="text-center mb-4">
                            <img src="/logos/logo-complet.avif" alt="Logo" className="logo" style={{ maxWidth: '100%' }} />
                        </div>

                        {success ? (
                            <div>
                                <div className="alert alert-success" role="alert">
                                    Tu cuenta ha sido configurada correctamente.
                                </div>
                                <div className="text-center mt-4">
                                    <Link to="/" className="btn btn-primary w-100 py-2">
                                        Iniciar sesión
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <>
                                <h5 className="text-center mb-3">Configura tu cuenta</h5>
                                <p className="text-muted text-center mb-4" style={{ fontSize: '0.9rem' }}>
                                    Introduce una contraseña para acceder a tu cuenta.
                                </p>

                                {errors.general && (
                                    <div className="alert alert-danger" role="alert">
                                        {errors.general}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="password" className="form-label">Contraseña</label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                            id="password"
                                            name="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="••••••••"
                                            disabled={loading}
                                        />
                                        {errors.password && (
                                            <div className="invalid-feedback">{errors.password}</div>
                                        )}
                                        <small className="text-muted d-block mt-1">
                                            Mínimo 8 caracteres, 1 mayúscula, 1 minúscula y 1 número
                                        </small>
                                    </div>

                                    <div className="mb-3">
                                        <label htmlFor="confirmPassword" className="form-label">Confirmar contraseña</label>
                                        <input
                                            type="password"
                                            className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleChange}
                                            placeholder="••••••••"
                                            disabled={loading}
                                        />
                                        {errors.confirmPassword && (
                                            <div className="invalid-feedback">{errors.confirmPassword}</div>
                                        )}
                                    </div>

                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100 py-2 mt-3"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Configurando...
                                            </>
                                        ) : 'Configurar mi cuenta'}
                                    </button>
                                </form>

                                <div className="text-center mt-3">
                                    <Link to="/" className="link-primary">
                                        Volver al inicio de sesión
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SetPassword;
